import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react'
import { useSelector } from 'react-redux'
import {
  KeyDownContextType,
  KeyHandler,
  KeyTypeEnum,
  KeyTypeValue,
  Subscribers,
} from './types'
import { findKeyEvent } from './utils'

const KeyDownContext = createContext<KeyDownContextType | undefined>(undefined)

const KeyDownProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const subscribers = useRef<Subscribers>({})

  // TODO: Delete this Workaround when Screensaver is migrated to new focus
  const showingScreensaver = useSelector(
    (state: any) => state.screensaver.showing
  )

  const keyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      const eventKey = findKeyEvent(event)
      if (!eventKey) {
        const keyEvent = event.key || event.keyCode || event.which
        console.warn(`Key event not found in KEY_MAP: ${keyEvent}`)
        return
      }

      if (showingScreensaver) {
        return
      }

      const currentHandlers = [
        ...(subscribers.current[eventKey as KeyTypeValue] || []),
      ]

      const stopPropagationHandlers = currentHandlers.filter(
        (singleHandler) => singleHandler.options?.stopPropagation
      )
      const stopPropagationHandler =
        stopPropagationHandlers[stopPropagationHandlers.length - 1]

      if (stopPropagationHandler) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        stopPropagationHandler.callback(eventKey as KeyTypeValue)
        return
      }

      if (eventKey === KeyTypeEnum.Back) {
        event.preventDefault()
      }

      for (const { callback } of currentHandlers) {
        callback(eventKey as KeyTypeValue)
      }
    },
    [subscribers.current, showingScreensaver]
  )

  useEffect(() => {
    window.addEventListener('keydown', keyDownHandler, {
      capture: true,
    })
    return () => {
      window.removeEventListener('keydown', keyDownHandler, {
        capture: true,
      })
    }
  }, [keyDownHandler])

  const subscribe = useCallback(
    (key: KeyTypeValue, handler: KeyHandler): (() => void) => {
      subscribers.current = {
        ...subscribers.current,
        [key]: [...(subscribers.current[key] || []), handler],
      }

      return () => {
        const currentHandlers = [...(subscribers.current[key] || [])]
        if (currentHandlers) {
          subscribers.current = {
            ...subscribers.current,
            [key]: currentHandlers.filter(
              (singleHandler) => singleHandler.callback !== handler.callback
            ),
          }

          if (currentHandlers.length === 0) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [key]: _, ...rest } = subscribers.current
            subscribers.current = rest
          }
        }
      }
    },
    [subscribers.current]
  )

  return (
    <KeyDownContext.Provider value={{ subscribe }}>
      {children}
    </KeyDownContext.Provider>
  )
}

export const useKeyDown = (
  keys: KeyTypeValue | KeyTypeValue[],
  callback: (key?: KeyTypeValue) => void,
  options?: { stopPropagation?: boolean }
) => {
  const context = useContext(KeyDownContext)

  if (!context) {
    throw new Error('useKeyDown must be used within a KeyDownProvider')
  }

  const { subscribe } = context

  const memoizedCallback = useCallback(callback, [callback])

  useEffect(() => {
    const keyArray = Array.isArray(keys) ? keys : [keys]
    const unsubscribes = keyArray.map((key) =>
      subscribe(key, { callback: memoizedCallback, options })
    )
    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe())
    }
  }, [keys, memoizedCallback, options, subscribe])
}

export default KeyDownProvider
